/* What a brushed range contains, and what can be done with it.
 *
 * Appears only when there is a selection. A control that is always visible but
 * usually does nothing is chrome; one that appears when it becomes true is
 * information -- the same reasoning that keeps ViewBar hidden until zoomed.
 *
 * The breakdown is the point of this component, not the buttons. A ten-minute
 * selection that is four minutes in storage, five on the card and one gone is
 * three different offers, and a single "Fetch" button over the top of that
 * would be wrong in two of the three cases. So the split is stated first and
 * every action is labelled with the part of the selection it actually covers.
 */
import { fmtBytes } from '../api'
import { fmtSpan, stamp, SNAP_S, type Selection } from '../charts'
import type { AvailabilityHour, NodeSnapshot } from './LevelsChart'

const HOUR_S = 3600

export interface Coverage {
  /** Seconds of the selection that are in object storage. */
  stored: number
  /** Seconds held on the node's card and not in storage. */
  onNode: number
  /** Seconds the node is no longer holding: unreachable. */
  gone: number
  /** Seconds the node has not reported on yet. */
  unknown: number
  /** Estimated bytes for the part that is on the node only. */
  nodeBytes: number
}

/** Split a selection across the availability states, weighted by overlap.
 *
 *  Hour buckets rarely line up with a selection, so every figure is prorated
 *  rather than counted whole. Charging a whole hour to a selection that clipped
 *  two minutes of it would overstate a request by an order of magnitude, and
 *  the byte figure is what someone decides against.
 */
export function coverageOf(
  sel: Selection, hours: AvailabilityHour[], node: NodeSnapshot,
): Coverage {
  const byHour = new Map<number, AvailabilityHour>()
  for (const h of hours) byHour.set(Math.floor(h.t / HOUR_S) * HOUR_S, h)

  const oldest = node.oldest_present_utc
    ? new Date(node.oldest_present_utc).getTime() / 1000 : null
  const observed = node.observed_utc
    ? new Date(node.observed_utc).getTime() / 1000 : null

  const out: Coverage = { stored: 0, onNode: 0, gone: 0, unknown: 0, nodeBytes: 0 }
  const first = Math.floor(sel.t0 / HOUR_S) * HOUR_S

  for (let t = first; t < sel.t1; t += HOUR_S) {
    const overlap = Math.min(sel.t1, t + HOUR_S) - Math.max(sel.t0, t)
    if (overlap <= 0) continue
    const frac = overlap / HOUR_S
    const row = byHour.get(t)

    if (!row || (!row.node_seconds && !row.cloud_seconds)) {
      // The same three-way split the strip draws, and for the same reason: an
      // hour nobody has reported on is not an hour known to be empty.
      if (observed !== null && t >= observed - HOUR_S) out.unknown += overlap
      else if (oldest !== null && t < oldest) out.gone += overlap
      else out.gone += overlap
      continue
    }

    const stored = row.cloud_seconds * frac
    const held = row.node_seconds * frac
    out.stored += Math.min(stored, overlap)
    out.onNode += Math.max(0, Math.min(held, overlap) - stored)
    // The remainder of the hour that neither has: a gap inside a reported hour.
    out.gone += Math.max(0, overlap - Math.max(stored, Math.min(held, overlap)))
    if (row.node_bytes_estimate !== null && row.node_seconds > 0) {
      const perSecond = row.node_bytes_estimate / row.node_seconds
      out.nodeBytes += perSecond * Math.max(0, Math.min(held, overlap) - stored)
    }
  }
  return out
}

function mins(s: number): string {
  return s >= 90 ? `${Math.round(s / 60)} min` : `${Math.round(s)} s`
}

/** Why an action cannot be taken, or null if it can.
 *
 *  Returned as a sentence rather than a boolean because every one of these
 *  reasons is different and an operator has to act differently on each: wait,
 *  ask someone for access, select somewhere else, or stop waiting because the
 *  thing was never going to happen. A greyed button with no explanation makes
 *  all four look like a bug.
 */
function blockedBecause(
  handler: (() => void) | undefined, storedSeconds: number,
): string | null {
  if (!handler) return 'Not built yet.'
  if (storedSeconds <= 0) return 'Nothing in this range is in storage.'
  return null
}

export function SelectionBar({
  sel, hours, node, canWrite, onClear, onListen, onSave, onPull,
  pullBusy, pullNote,
}: {
  sel: Selection
  hours: AvailabilityHour[]
  node: NodeSnapshot
  canWrite: boolean
  onClear: () => void
  /** Undefined means not implemented, and the button says so. */
  onListen?: () => void
  onSave?: () => void
  onPull?: () => void
  pullBusy?: boolean
  /** What the server said back — including that it trimmed the request. */
  pullNote?: string | null
}) {
  const c = coverageOf(sel, hours, node)
  const span = sel.t1 - sel.t0
  const segments = Math.round(span / SNAP_S)
  const listenBlock = blockedBecause(onListen, c.stored)
  const saveBlock = blockedBecause(onSave, c.stored)

  return (
    <div className="card selbar">
      <div className="card-body stack">
        <div className="row selbar-head">
          <div>
            <strong className="num">{stamp(sel.t0)}</strong>
            <span className="faint"> → </span>
            <strong className="num">{stamp(sel.t1)}</strong>
          </div>
          <span className="faint num">
            {fmtSpan(span)} · {segments} segment{segments === 1 ? '' : 's'}
          </span>
          <button className="btn ghost sm" onClick={onClear}
            style={{ marginLeft: 'auto' }}>Clear</button>
        </div>

        <p className="faint" style={{ margin: 0, fontSize: 13 }}>
          {c.stored > 0 && <><i className="sw cloud" /> {mins(c.stored)} in storage </>}
          {c.onNode > 0 && (
            <><i className="sw node" /> {mins(c.onNode)} on the card
              {c.nodeBytes > 0 && <> (~{fmtBytes(Math.round(c.nodeBytes))})</>} </>
          )}
          {c.gone > 0 && <><i className="sw gap" /> {mins(c.gone)} gone </>}
          {c.unknown > 0 && <><i className="sw unknown" /> {mins(c.unknown)} not reported </>}
        </p>

        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <button className="btn primary sm"
            disabled={!!listenBlock} onClick={onListen}
            title={listenBlock ?? `Play the ${mins(c.stored)} that is in storage`}>
            Listen
          </button>
          <button className="btn sm"
            disabled={!!saveBlock} onClick={onSave}
            title={saveBlock ?? `Save the ${mins(c.stored)} that is in storage`}>
            Save…
          </button>
          <button className="btn sm"
            disabled={!onPull || !canWrite || pullBusy || c.onNode <= 0}
            onClick={onPull}
            title={!onPull
              ? 'Not built yet — a node cannot be asked for a time range.'
              : !canWrite ? 'You do not have write access on this project.'
              : c.onNode <= 0
                ? 'None of this range is on the node to ask for.'
                : `Ask the node for the ${mins(c.onNode)} it is still holding`}>
            {pullBusy ? 'Asking…' : 'Ask the node for this'}
          </button>
        </div>

        {pullNote && <p className="notice" style={{ margin: 0 }}>{pullNote}</p>}

        {/* Said once, under the buttons, rather than only in a tooltip: a
            tooltip is unreachable on the phone this is used from. */}
        {(listenBlock || saveBlock) && (
          <p className="faint" style={{ margin: 0, fontSize: 12 }}>
            {listenBlock === saveBlock
              ? listenBlock
              : `Listen: ${listenBlock ?? 'ready'}. Save: ${saveBlock ?? 'ready'}.`}
            {c.stored <= 0 && c.onNode > 0
              && ' Audio reaches storage only when someone asks for it, or at the'
                 + ' full tier; this node is not on it.'}
          </p>
        )}

        {/* Why the third button is dark, said once and plainly. An operator
            looking at five minutes of audio sitting on a card needs to know
            whether the button is broken, unauthorised, or unbuilt. */}
        {c.onNode > 0 && (
          <p className="faint" style={{ margin: 0, fontSize: 12 }}>
            {mins(c.onNode)} of this is on the node's card and cannot be
            reached yet: there is no inbound path to a node, and asking one to
            send a range is not built. It will be evicted
            {node.retention_hours ? ` about ${Math.round(node.retention_hours)} hours` : ' a day'}
            {' '}after it was recorded, whether or not anyone asked for it.
            {!canWrite && ' You also do not have write access on this project.'}
          </p>
        )}

        {c.unknown > 0 && node.age_seconds !== null && (
          <p className="faint" style={{ margin: 0, fontSize: 12 }}>
            Part of this range is newer than the node's last report
            ({Math.round(node.age_seconds / 60)} min ago), so what it holds
            there is not known rather than known to be empty.
          </p>
        )}
      </div>
    </div>
  )
}
